import { InfoCard, type InfoCardProps } from '@/components/contracts/InfoCard'
import { CardDisclosureTrigger } from '@/components/patterns/card-disclosure'
import { Card, CardContent } from '@/components/ui/card'
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible'
import { ScrollArea } from '@/components/ui/scroll-area'
import { Typography } from '@/components/ui/typography'
import type { PathLink } from '@/hooks/usePathHelper'
import type {
  BookedSalesComponentKey,
  SalesSummaryReportFinancialBreakdown,
  SalesSummaryReportSalesBreakdown,
  SalesSummaryReportSalesBreakdownLine,
  SalesSummaryReportSummary,
} from '@/lib/services/reports/reports.types'
import { type NumericInput, cn, formatCurrency, parseCurrency } from '@/lib/utils'
import { Link } from '@tanstack/react-router'
import {
  BadgeDollarSign,
  ChevronDown,
  ExternalLink,
  RotateCcw,
  ReceiptText,
  Users,
} from 'lucide-react'
import { SectionCardHeader } from './-section-card-header'

type LedgerTone = 'positive' | 'negative' | 'neutral'

/** A link to a related report; null when the report has no date range to carry over. */
type ReportLink = PathLink | null

/** A line inside a ledger section, or a link to a related report (which has no value). */
type LedgerDetail = {
  key: string
  title: string
  value?: string
  valueTone?: LedgerTone
  link?: ReportLink
  linkAriaLabel?: string
}

type LedgerSection = {
  key: string
  title: string
  description?: string
  value: string
  valueTone: LedgerTone
  defaultOpen?: boolean
  details: LedgerDetail[]
}

type LedgerTotal = {
  title: string
  description: string
  value: string
  emphasized?: boolean
}

type FinancialWalkthroughEntry =
  | ({ kind: 'section' } & LedgerSection)
  | ({ kind: 'summary'; key: string } & LedgerTotal)

type SummaryMetricCardData = Omit<InfoCardProps, 'link'> & { key: string }

type SummaryMetricsSummary = Pick<
  SalesSummaryReportSummary,
  'total_sales' | 'paid_order_count' | 'average_ticket' | 'refund_count'
>

type SalesSummaryPrimaryMetricsProps = {
  summary: SummaryMetricsSummary
  isOrderBased?: boolean
  customerCount?: number
  /** Total sales across the customers, parsed. */
  customerSalesTotal?: number
  isGlobalReport?: boolean
  organizationCount?: number
  /** Total sales across the florists, parsed. */
  organizationSalesTotal?: number
}

export function SalesSummaryPrimaryMetrics({
  summary,
  isOrderBased = false,
  customerCount = 0,
  customerSalesTotal = 0,
  isGlobalReport = false,
  organizationCount = 0,
  organizationSalesTotal = 0,
}: SalesSummaryPrimaryMetricsProps) {
  const cards: SummaryMetricCardData[] = isOrderBased
    ? [
        {
          key: 'sales',
          title: 'Total Sales',
          value: formatCurrency(summary.total_sales),
          icon: BadgeDollarSign,
          tone: 'success',
          accent: 'success',
        },
        {
          key: 'orders',
          title: 'Orders',
          value: String(summary.paid_order_count ?? 0),
          icon: ReceiptText,
          tone: 'periwinkle',
        },
        {
          key: 'average_order',
          title: 'Avg Per Order',
          value: formatCurrency(summary.average_ticket),
          icon: BadgeDollarSign,
          tone: 'amethyst',
        },
        {
          key: 'average_customer',
          title: 'Avg Per Customer',
          value: formatCurrency(customerCount ? customerSalesTotal / customerCount : 0),
          icon: Users,
          tone: 'periwinkle',
        },
      ]
    : buildPrimarySummaryCards({
        summary,
        customerCount,
        customerSalesTotal,
        isGlobalReport,
        organizationCount,
        organizationSalesTotal,
      })

  return (
    <div className="grid grid-cols-1 gap-4 @2xl/sales-results:grid-cols-2 @5xl/sales-results:grid-cols-4">
      {cards.map((card) => (
        <SummaryMetricCard key={card.key} card={card} />
      ))}
    </div>
  )
}

type SalesSummaryFinancialBreakdownProps = {
  summary: Pick<SalesSummaryReportSummary, 'total_sales' | 'net_sales'>
  /** Null on the orders basis. */
  financialBreakdown: SalesSummaryReportFinancialBreakdown | null | undefined
  isGlobalReport?: boolean
  salesTaxLink?: ReportLink
  tipsReportLink?: ReportLink
  className?: string
}

export function SalesSummaryFinancialBreakdown({
  summary,
  financialBreakdown,
  isGlobalReport = false,
  salesTaxLink,
  tipsReportLink,
  className,
}: SalesSummaryFinancialBreakdownProps) {
  if (!financialBreakdown) return null

  const walkthrough = buildFinancialWalkthrough(
    summary,
    salesTaxLink,
    tipsReportLink,
    financialBreakdown,
    isGlobalReport
  )

  return (
    <Card className={cn('min-w-0', className)}>
      <SectionCardHeader
        title="Financial Breakdown"
        description={
          isGlobalReport
            ? 'How sales, refunds, and fees roll into payout'
            : 'Order payments, deposits, refunds, and fees'
        }
      />
      <CardContent className="min-[1540px]:min-h-0 min-[1540px]:flex-1 min-[1540px]:overflow-hidden">
        <ScrollArea className="w-full overflow-hidden rounded-lg border border-border/70 bg-muted/15 min-[1540px]:h-full">
          <div>
            {walkthrough.map((section, index) => {
              if (section.kind === 'summary') {
                const { key, kind: _kind, ...summarySection } = section

                return <FinancialLedgerTotal key={key} {...summarySection} bordered={index > 0} />
              }

              return (
                <FinancialLedgerSection key={section.key} section={section} bordered={index > 0} />
              )
            })}
          </div>
        </ScrollArea>
      </CardContent>
    </Card>
  )
}

// Sales Statistics keeps the familiar Financial Breakdown layout, limited to sales:
// sales before refunds, refunds, then total sales. Amounts are the Sales Summary's own
// period totals; fees and deposits live in the Credit Card Deposit Report.
const SALES_LINE_TITLES: Partial<Record<BookedSalesComponentKey, string>> = {
  subtotal: 'Products & Services',
  freight_fee: 'Delivery Fees',
  tax_amount: 'Tax Collected',
  relay_fee: 'Relay Fees Collected',
  tip_amount: 'Total Tips',
  discount: 'Discounts Applied',
}

const REFUND_LINE_TITLES: Partial<Record<BookedSalesComponentKey, string>> = {
  subtotal: 'Products & Services Refunded',
  freight_fee: 'Delivery Refunded',
  tax_amount: 'Tax Refunded',
  relay_fee: 'Relay Fees Refunded',
  tip_amount: 'Tips Refunded',
  discount: 'Discounts Reversed',
}

// Same line order as the original Financial Breakdown; other sources follow.
const LINE_ORDER: BookedSalesComponentKey[] = [
  'subtotal',
  'freight_fee',
  'onestem_payable',
  'tax_amount',
  'relay_fee',
  'tip_amount',
  'discount',
]

function orderLines(lines: SalesSummaryReportSalesBreakdownLine[]) {
  const rank = (line: SalesSummaryReportSalesBreakdownLine) => {
    const index = LINE_ORDER.indexOf(line.key)
    return index === -1 ? LINE_ORDER.length : index
  }

  return [...lines].sort((left, right) => rank(left) - rank(right))
}

type SalesStatisticsSalesBreakdownProps = {
  /** Null on the collections basis. */
  breakdown: SalesSummaryReportSalesBreakdown | null | undefined
  salesSummaryLink?: ReportLink
  salesTaxLink?: ReportLink
  tipsReportLink?: ReportLink
  depositsLink?: ReportLink
  className?: string
}

export function SalesStatisticsSalesBreakdown({
  breakdown,
  salesSummaryLink,
  salesTaxLink,
  tipsReportLink,
  depositsLink,
  className,
}: SalesStatisticsSalesBreakdownProps) {
  if (!breakdown) return null

  const reportLinks: Partial<
    Record<BookedSalesComponentKey, Pick<LedgerDetail, 'link' | 'linkAriaLabel'>>
  > = {
    tax_amount: { link: salesTaxLink, linkAriaLabel: 'Open sales tax report' },
    tip_amount: { link: tipsReportLink, linkAriaLabel: 'Open tips report' },
  }
  const toDetail =
    (titles: Partial<Record<BookedSalesComponentKey, string>>, suffix?: string) =>
    (line: SalesSummaryReportSalesBreakdownLine): LedgerDetail => ({
      key: line.key,
      title: titles[line.key] ?? (suffix ? `${line.label} ${suffix}` : line.label),
      value: formatCurrency(line.amount),
      valueTone: feeValueTone(line.amount),
      ...(reportLinks[line.key]?.link ? reportLinks[line.key] : {}),
    })
  const sectionCandidates: (LedgerSection | null)[] = [
    {
      key: 'before_refunds',
      title: 'Before refunds',
      description: 'Everything charged on orders placed in these dates',
      value: formatCurrency(breakdown.gross_sales),
      valueTone: 'positive',
      defaultOpen: true,
      details: orderLines(breakdown.sales).map(toDetail(SALES_LINE_TITLES)),
    },
    // Items added to or taken off orders placed earlier: neither a sale of the period nor a refund. Shown whenever a
    // component moved, even when the moves net to nothing (an even swap of an item for a delivery charge).
    breakdown.order_change_lines.length > 0 || parseCurrency(breakdown.order_changes) !== 0
      ? {
          key: 'order_changes',
          title: 'Order changes',
          description: 'Items added to or taken off orders placed earlier',
          value: formatCurrency(breakdown.order_changes),
          valueTone: feeValueTone(breakdown.order_changes),
          defaultOpen: false,
          details: orderLines(breakdown.order_change_lines).map(toDetail(SALES_LINE_TITLES)),
        }
      : null,
    {
      key: 'refunds',
      title: breakdown.has_cancellations ? 'Refunds & Cancellations' : 'Refunds',
      description: breakdown.has_cancellations
        ? 'Customer refunds and canceled balances, less any reversed refunds'
        : 'Customer refunds, less any reversed refunds',
      value: formatCurrency(breakdown.refunds),
      valueTone: feeValueTone(breakdown.refunds),
      defaultOpen: false,
      details: orderLines(breakdown.refund_lines).map(toDetail(REFUND_LINE_TITLES, 'Refunded')),
    },
    // Payout changes FSN accepted after an order was placed; neither a sale nor a refund.
    parseCurrency(breakdown.adjustments) !== 0
      ? {
          key: 'adjustments',
          title: 'Incoming wire order adjustments',
          description: 'Payout changes accepted for wire-in orders placed earlier',
          value: formatCurrency(breakdown.adjustments),
          valueTone: feeValueTone(breakdown.adjustments),
          defaultOpen: false,
          details: orderLines(breakdown.adjustment_lines ?? []).map(toDetail({})),
        }
      : null,
  ]
  const sections = sectionCandidates.filter((section) => section !== null)
  const relatedLinks = [
    salesSummaryLink
      ? {
          key: 'sales-summary',
          title: 'View daily detail',
          linkAriaLabel: 'View daily detail in the Sales Summary',
          link: salesSummaryLink,
        }
      : null,
    depositsLink
      ? {
          key: 'deposits',
          title: 'View credit card deposits',
          linkAriaLabel: 'View credit card deposits for these dates',
          link: depositsLink,
        }
      : null,
  ].filter((detail) => detail !== null)

  return (
    <Card className={cn('min-w-0', className)}>
      <SectionCardHeader title="Financial Breakdown" description="Sales, refunds, and discounts" />
      <CardContent className="space-y-3">
        <div className="overflow-hidden rounded-lg border border-border/70 bg-muted/15">
          {sections.map((section, index) => (
            <FinancialLedgerSection key={section.key} section={section} bordered={index > 0} />
          ))}
          <FinancialLedgerTotal
            title="Total sales"
            description="After refunds and discounts"
            value={formatCurrency(breakdown.net_sales)}
            emphasized
            bordered
          />
        </div>
        {relatedLinks.length > 0 ? (
          <div className="space-y-1">
            {relatedLinks.map((detail) => (
              <FinancialBreakdownDetailRow key={detail.key} detail={detail} />
            ))}
          </div>
        ) : null}
      </CardContent>
    </Card>
  )
}

// Summary metric cards have no links, so each renders as a plain InfoCard.
function SummaryMetricCard({ card }: { card: SummaryMetricCardData }) {
  const { key: _cardKey, ...infoCard } = card

  return <InfoCard {...infoCard} />
}

function FinancialLedgerSection({
  section,
  bordered = false,
}: {
  section: LedgerSection
  bordered?: boolean
}) {
  return (
    <Collapsible defaultOpen={section.defaultOpen}>
      <div className={cn(bordered && 'border-t border-border/70')}>
        <CollapsibleTrigger asChild>
          <CardDisclosureTrigger className="group grid grid-cols-[auto_minmax(0,1fr)_auto] items-center gap-3 px-4 py-3 sm:px-5">
            <ChevronDown className="size-4 text-muted-foreground transition-transform group-data-[state=open]:rotate-180" />
            <div className="min-w-0 flex-1">
              <div className="text-sm font-semibold text-foreground">{section.title}</div>
            </div>
            <div
              className={cn(
                'shrink-0 text-sm font-semibold tabular-nums',
                ledgerValueToneClass(section.valueTone)
              )}
            >
              {section.value}
            </div>
          </CardDisclosureTrigger>
        </CollapsibleTrigger>

        <CollapsibleContent>
          <div className="space-y-1 px-4 pb-3 sm:px-5">
            {section.description ? (
              <Typography variant="description">{section.description}</Typography>
            ) : null}
            {section.details.map((detail) => (
              <FinancialBreakdownDetailRow key={detail.key} detail={detail} />
            ))}
          </div>
        </CollapsibleContent>
      </div>
    </Collapsible>
  )
}

function FinancialBreakdownDetailRow({ detail }: { detail: LedgerDetail }) {
  const content = (
    <div
      className={cn(
        'grid grid-cols-[minmax(0,1fr)_auto] items-center gap-4 rounded-md px-7 py-2 transition-colors sm:px-9',
        detail.link ? 'hover:bg-muted/20' : ''
      )}
    >
      <div
        className={cn(
          'min-w-0 text-sm text-muted-foreground',
          detail.link
            ? 'flex items-center gap-2 text-foreground underline decoration-dotted underline-offset-4'
            : ''
        )}
      >
        <span>{detail.title}</span>
        {detail.link ? <ExternalLink className="size-3.5 shrink-0 text-muted-foreground" /> : null}
      </div>
      <div
        className={cn(
          'shrink-0 text-sm font-medium tabular-nums',
          ledgerValueToneClass(detail.valueTone)
        )}
      >
        {detail.value}
      </div>
    </div>
  )

  if (!detail.link) {
    return content
  }

  return (
    <Link
      {...detail.link}
      aria-label={detail.linkAriaLabel ?? 'Open report'}
      className="block cursor-pointer rounded-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background"
    >
      {content}
    </Link>
  )
}

function FinancialLedgerTotal({
  title,
  description,
  value,
  emphasized = false,
  bordered = false,
}: LedgerTotal & { bordered?: boolean }) {
  return (
    <div
      className={cn(
        'px-4 py-4 sm:px-5',
        bordered && 'border-t border-border/70',
        emphasized && 'bg-muted/30'
      )}
    >
      <div className="grid grid-cols-[minmax(0,1fr)_auto] items-center gap-4">
        <div className="min-w-0">
          <div
            className={cn(
              'text-sm font-semibold uppercase tracking-[0.12em]',
              emphasized ? 'text-foreground' : 'text-muted-foreground'
            )}
          >
            {title}
          </div>
          <Typography variant="caption" className="mt-1 block max-w-md">
            {description}
          </Typography>
        </div>
        <div
          className={cn(
            'shrink-0 text-lg font-semibold tabular-nums sm:text-xl',
            emphasized ? 'text-interaction' : 'text-foreground'
          )}
        >
          {value}
        </div>
      </div>
    </div>
  )
}

function buildPrimarySummaryCards({
  summary,
  customerCount,
  customerSalesTotal,
  isGlobalReport,
  organizationCount,
  organizationSalesTotal,
}: Required<Omit<SalesSummaryPrimaryMetricsProps, 'isOrderBased'>>): SummaryMetricCardData[] {
  const refundCount = Number(summary.refund_count ?? 0)
  const averageCustomerValue =
    customerCount > 0 ? Number(customerSalesTotal ?? 0) / customerCount : 0
  const averageOrganizationValue =
    organizationCount > 0 ? Number(organizationSalesTotal ?? 0) / organizationCount : 0

  return [
    {
      key: isGlobalReport ? 'average_florist_value' : 'average_customer_value',
      title: isGlobalReport ? 'Avg Sales Per Florist' : 'Avg Per Customer',
      description: isGlobalReport ? 'Average sales per florist' : 'Order payments per customer',
      value: formatCurrency(isGlobalReport ? averageOrganizationValue : averageCustomerValue),
      icon: Users,
      tone: 'periwinkle',
    },
    {
      key: 'average_ticket',
      title: isGlobalReport ? 'Avg Sales Per Order' : 'Avg Per Order',
      description: isGlobalReport ? 'Average sales per paid order' : 'Order payments per order',
      value: formatCurrency(summary.average_ticket),
      icon: BadgeDollarSign,
      tone: 'amethyst',
    },
    {
      key: 'paid_order_count',
      title: isGlobalReport ? 'Order Count' : 'Orders With Payments',
      description: isGlobalReport ? 'Paid orders in this range' : 'Includes partial payments',
      value: String(summary.paid_order_count ?? 0),
      icon: ReceiptText,
      tone: 'periwinkle',
    },
    {
      key: 'refund_count',
      title: 'Refund Count',
      description: 'Number of refunds in this range',
      value: String(refundCount),
      icon: RotateCcw,
      tone: 'destructive',
    },
  ]
}

function buildFinancialWalkthrough(
  summary: SalesSummaryFinancialBreakdownProps['summary'],
  salesTaxLink: ReportLink | undefined,
  tipsReportLink: ReportLink | undefined,
  financialBreakdown: SalesSummaryReportFinancialBreakdown,
  isGlobalReport: boolean
): FinancialWalkthroughEntry[] {
  // The API owns financial arithmetic. Report rows may be filtered or paginated.
  const hasServiceFeeActivity = financialBreakdown.has_service_fee_activity
  const salesSubtotal = financialBreakdown.sales_subtotal
  const salesDelivery = financialBreakdown.sales_delivery
  const salesTax = financialBreakdown.sales_tax
  const salesTips = financialBreakdown.sales_tips
  const salesRelayFees = financialBreakdown.sales_relay_fees
  const refundsAmount = financialBreakdown.refunds
  const feesPaidValue = -parseCurrency(financialBreakdown.fees)
  const salesDiscounts = absoluteAmount(financialBreakdown.sales_discounts)
  const refundDiscounts = financialBreakdown.refund_discounts
  const refundSubtotal = financialBreakdown.refund_subtotal
  const refundDelivery = financialBreakdown.refund_delivery
  const refundTax = financialBreakdown.refund_tax
  const refundTips = financialBreakdown.refund_tips
  const refundRelayFees = financialBreakdown.refund_relay_fees
  const feeDetails = buildFeeDetails(financialBreakdown)
  const startingSales = financialBreakdown.gross_sales
  const netTaxToSetAside = absoluteAmount(financialBreakdown.tax_set_aside)
  const taxRefundSetAsideOffset = financialBreakdown.tax_set_aside_refunded
  const readyToSpend = financialBreakdown.take_home

  return [
    {
      kind: 'section',
      key: 'starting_sales',
      title: 'Before refunds & fees',
      description: isGlobalReport
        ? 'Everything before deductions'
        : 'Order payments before refunds and fees',
      value: formatCurrency(startingSales),
      valueTone: 'positive',
      defaultOpen: true,
      details: [
        {
          key: 'products_and_services',
          title: 'Products & Services',
          value: formatCurrency(salesSubtotal),
          valueTone: 'neutral',
        },
        {
          key: 'delivery_fees',
          title: 'Delivery Fees',
          value: formatCurrency(salesDelivery),
          valueTone: 'neutral',
        },
        ...(hasServiceFeeActivity
          ? ([
              {
                key: 'service_fee',
                title: 'Service Fee',
                value: formatCurrency(financialBreakdown.sales_service_fee),
                valueTone: 'neutral',
              },
            ] satisfies LedgerDetail[])
          : []),
        {
          key: 'tax_collected',
          title: 'Tax Collected',
          value: formatCurrency(salesTax),
          valueTone: 'neutral',
          link: salesTaxLink,
          linkAriaLabel: 'Open sales tax report',
        },
        {
          key: 'relay_fees_collected',
          title: 'Relay Fees Collected',
          value: formatCurrency(salesRelayFees),
          valueTone: 'neutral',
        },
        {
          key: 'total_tips',
          title: 'Total Tips',
          value: formatCurrency(salesTips),
          valueTone: 'neutral',
          link: tipsReportLink,
          linkAriaLabel: 'Open tips report',
        },
        {
          key: 'order_discounts',
          title: 'Discounts Applied',
          value: formatNegativeMoney(salesDiscounts),
          valueTone: salesDiscounts > 0 ? 'negative' : 'neutral',
        },
      ],
    },
    {
      kind: 'section',
      key: 'refunds',
      title: 'Refunds',
      description: 'Customer refunds, less any reversed refunds',
      value: formatCurrency(refundsAmount),
      valueTone: feeValueTone(refundsAmount),
      defaultOpen: false,
      details: [
        {
          key: 'refund_products_and_services',
          title: 'Products & Services Refunded',
          value: formatCurrency(refundSubtotal),
          valueTone: feeValueTone(refundSubtotal),
        },
        {
          key: 'refund_delivery_fees',
          title: 'Delivery Refunded',
          value: formatCurrency(refundDelivery),
          valueTone: feeValueTone(refundDelivery),
        },
        ...(hasServiceFeeActivity
          ? [
              {
                key: 'refund_service_fee',
                title: 'Service Fee Refunded',
                value: formatCurrency(financialBreakdown.refund_service_fee),
                valueTone: feeValueTone(financialBreakdown.refund_service_fee),
              },
            ]
          : []),
        {
          key: 'refund_tax',
          title: 'Tax Refunded',
          value: formatCurrency(refundTax),
          valueTone: feeValueTone(refundTax),
        },
        {
          key: 'refund_relay_fees',
          title: 'Relay Fees Refunded',
          value: formatCurrency(refundRelayFees),
          valueTone: feeValueTone(refundRelayFees),
        },
        {
          key: 'refund_tips',
          title: 'Tips Refunded',
          value: formatCurrency(refundTips),
          valueTone: feeValueTone(refundTips),
        },
        {
          key: 'refund_discounts',
          title: 'Discounts Reversed',
          value: formatCurrency(refundDiscounts),
          valueTone: feeValueTone(refundDiscounts),
        },
      ],
    },
    {
      kind: 'summary',
      key: 'total_sales',
      title: isGlobalReport ? 'Net Sales' : 'Order payments',
      description: 'After refunds and discounts, before fees',
      value: formatCurrency(summary.total_sales),
    },
    ...(parseCurrency(financialBreakdown.deposits) !== 0
      ? ([
          {
            kind: 'section',
            key: 'deposits',
            title: 'Deposits',
            description:
              'Money received for unfinished orders, less deposits returned or applied to sales',
            value: formatCurrency(financialBreakdown.deposits),
            valueTone: parseCurrency(financialBreakdown.deposits) > 0 ? 'positive' : 'negative',
            details: [],
          },
        ] satisfies FinancialWalkthroughEntry[])
      : []),
    {
      kind: 'section',
      key: 'fees_paid',
      title: 'Fees',
      description:
        feesPaidValue < 0 ? 'Fee credits returned on refunds' : 'Fees taken out before payout',
      value: formatFeeImpact(feesPaidValue),
      valueTone: feesPaidValue === 0 ? 'neutral' : feesPaidValue < 0 ? 'positive' : 'negative',
      defaultOpen: feesPaidValue !== 0,
      details: feeDetails,
    },
    ...(hasServiceFeeActivity
      ? ([
          {
            kind: 'section',
            key: 'onestem_withholding',
            title: 'Collected for OneStem',
            value: formatCurrency(financialBreakdown.onestem_withholding),
            valueTone: feeValueTone(financialBreakdown.onestem_withholding),
            defaultOpen: true,
            details: [
              {
                key: 'service_fee_withheld',
                title: 'Service Fee',
                value: formatCurrency(financialBreakdown.service_fee_withheld),
                valueTone: feeValueTone(financialBreakdown.service_fee_withheld),
              },
              {
                key: 'service_fee_tax_withheld',
                title: 'Service Fee Tax',
                value: formatCurrency(financialBreakdown.service_fee_tax_withheld),
                valueTone: feeValueTone(financialBreakdown.service_fee_tax_withheld),
              },
            ],
          },
        ] satisfies FinancialWalkthroughEntry[])
      : []),
    {
      kind: 'summary',
      key: 'net_sales',
      title: 'Payout',
      description: isGlobalReport
        ? 'What hits your account before sales tax is set aside'
        : 'After fees, including cash and checks',
      value: formatCurrency(summary.net_sales),
    },
    {
      kind: 'section',
      key: 'tax_set_aside',
      title: 'Sales Tax to Set Aside',
      description: 'Your collected sales tax, less refunds',
      value: formatNegativeMoney(netTaxToSetAside),
      valueTone: netTaxToSetAside > 0 ? 'negative' : 'neutral',
      defaultOpen: false,
      details: [
        {
          key: 'tax_set_aside_collected',
          title: 'Tax Collected',
          value: formatCurrency(financialBreakdown.tax_set_aside_collected),
          valueTone: feeValueTone(financialBreakdown.tax_set_aside_collected),
          link: salesTaxLink,
          linkAriaLabel: 'Open sales tax report',
        },
        {
          key: 'tax_set_aside_refunded',
          title: 'Tax Refunded',
          value: formatCurrency(taxRefundSetAsideOffset),
          valueTone: feeValueTone(taxRefundSetAsideOffset),
          link: salesTaxLink,
          linkAriaLabel: 'Open sales tax report',
        },
      ],
    },
    {
      kind: 'summary',
      key: 'available_cash',
      title: isGlobalReport ? 'Take-Home' : 'After fees & sales tax',
      description: 'Total after sales tax is set aside',
      value: formatCurrency(readyToSpend),
      emphasized: true,
    },
  ]
}

type FeeDetailKey = 'website_platform_fees' | 'pos_platform_fees' | 'merchant_fees' | 'other_fees'

function buildFeeDetails(financialBreakdown: SalesSummaryReportFinancialBreakdown): LedgerDetail[] {
  const fees: [key: FeeDetailKey, title: string][] = [
    ['website_platform_fees', 'Website Platform Fee'],
    ['pos_platform_fees', 'POS Platform Fee'],
    ['merchant_fees', 'Merchant Fee'],
    ['other_fees', 'Other Fees'],
  ]

  return fees
    .filter(
      ([key]) => key !== 'other_fees' || Math.abs(parseCurrency(financialBreakdown[key])) >= 0.01
    )
    .map(([key, title]) => ({
      key,
      title,
      value: formatCurrency(financialBreakdown[key]),
      valueTone: feeValueTone(financialBreakdown[key]),
    }))
}

function normalizeMoney(value: NumericInput) {
  const amount = Math.round(parseCurrency(value) * 100) / 100

  return Object.is(amount, -0) ? 0 : amount
}

function absoluteAmount(value: NumericInput) {
  return Math.abs(parseCurrency(value))
}

function formatNegativeMoney(value: NumericInput) {
  const amount = absoluteAmount(value)

  return formatCurrency(amount > 0 ? -amount : 0)
}

function formatFeeImpact(value: number) {
  const amount = normalizeMoney(value)

  if (amount < 0) {
    return formatCurrency(Math.abs(amount))
  }

  return formatNegativeMoney(amount)
}

function feeValueTone(value: NumericInput): LedgerTone {
  const amount = normalizeMoney(value)

  if (amount < 0) {
    return 'negative'
  }

  if (amount > 0) {
    return 'positive'
  }

  return 'neutral'
}

function ledgerValueToneClass(tone: LedgerTone | undefined) {
  if (tone === 'negative') {
    return 'text-destructive'
  }

  if (tone === 'positive') {
    return 'text-foreground'
  }

  return 'text-foreground'
}
